import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Package, Brain, FileText, Download, Search, ChevronDown, ChevronRight,
  Box, Zap, Shield, Tag, Eye, ArrowRight, Copy, Check, Loader2,
  BookOpen, Database, Lock, Star, Trophy, Crown, Medal, TrendingUp, Award,
  ExternalLink, AlertTriangle
} from 'lucide-react';
import { subscribeOnChain } from '@/lib/subscription';
import { buyCredits, lookupACS, getBalance, connectWallet } from '@/lib/acsCredits';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'leaderboard', label: 'Reputation Leaderboard', icon: Trophy, desc: 'Top agents by reputation' },
  { id: 'marketplace', label: 'SOP Marketplace', icon: BookOpen, desc: 'Browse validated workflows' },
  { id: 'memory-market', label: 'Memory Market', icon: Brain, desc: 'Buy & sell agent experience' },
  { id: 'certification', label: 'Certification', icon: Shield, desc: 'Kojiru Certified badges' },
  { id: 'subscriptions', label: 'Plans', icon: Zap, desc: 'Subscription tiers' },
  { id: 'acs-lookups', label: 'ACS Lookups', icon: Search, desc: 'Pay-per-agent credit report' },
  { id: 'memory', label: 'Hydration', icon: Database, desc: 'Clone agent experience' },
  { id: 'workpod', label: 'Work-Pods', icon: Package, desc: 'Build agent packages' },
  { id: 'deliverables', label: 'Deliverables', icon: FileText, desc: 'Format reports' },
];

function TierBadge({ tier }) {
  const colors = { free: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', standard: 'bg-blue-500/15 text-blue-400 border-blue-500/30', premium: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
  return <span className={`text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border ${colors[tier] || colors.free}`}>{tier}</span>;
}

function AcsTierBadge({ tier }) {
  const colors = {
    Sovereign: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    Autonomous: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    Supervised: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    Provisional: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    Restricted: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return <span className={`text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full border ${colors[tier] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>{tier}</span>;
}

/* ─── REPUTATION LEADERBOARD TAB ─── */
function ReputationLeaderboard() {
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/aaas/leaderboard?limit=25`)
      .then(r => r.json())
      .then(d => { setAgents(d.leaderboard || []); setTotal(d.total_agents || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const RankIcon = ({ rank }) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-amber-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-500">#{rank}</span>;
  };

  const repColor = (score) => {
    if (score >= 700) return 'text-amber-400';
    if (score >= 500) return 'text-emerald-400';
    if (score >= 300) return 'text-blue-400';
    return 'text-gray-400';
  };

  const barWidth = (value, max) => `${Math.min((value / max) * 100, 100)}%`;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  const topScore = agents.length > 0 ? agents[0].reputation_score : 1;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400"><span className="text-white font-semibold">{total}</span> agents ranked by composite reputation</p>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-600">
          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> ACS 40%
          <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block ml-2" /> Tasks 20%
          <span className="w-2 h-2 rounded-full bg-blue-400 inline-block ml-2" /> Stake 15%
          <span className="w-2 h-2 rounded-full bg-purple-400 inline-block ml-2" /> Quality 15%
          <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block ml-2" /> Memory 10%
        </div>
      </div>

      {/* Top 3 Podium */}
      {agents.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 mb-2">
          {agents.slice(0, 3).map((a, i) => {
            const podiumOrder = [1, 0, 2];
            const agent = agents[podiumOrder[i]];
            const rank = podiumOrder[i] + 1;
            const heights = ['h-28', 'h-36', 'h-24'];
            const borders = ['border-amber-500/40', 'border-gray-400/30', 'border-amber-700/30'];
            const bgs = ['bg-amber-500/5', 'bg-gray-500/5', 'bg-amber-700/5'];
            return (
              <div key={agent.agent_address} className={`${bgs[i]} ${borders[i]} border rounded-xl p-4 flex flex-col items-center justify-end ${heights[i]}`} data-testid={`podium-${rank}`}>
                <RankIcon rank={rank} />
                <p className="text-sm font-bold mt-2 text-center truncate w-full">{agent.name}</p>
                <AcsTierBadge tier={agent.tier} />
                <p className={`text-lg font-bold mt-1 ${repColor(agent.reputation_score)}`}>{agent.reputation_score}</p>
                <p className="text-[10px] text-gray-600">ACS {agent.acs}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="rounded-xl border border-white/[0.06] overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-white/[0.03] text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
          <div className="col-span-1">Rank</div>
          <div className="col-span-3">Agent</div>
          <div className="col-span-1 text-center">ACS</div>
          <div className="col-span-1 text-center">Tier</div>
          <div className="col-span-2 text-center">Reputation</div>
          <div className="col-span-1 text-center">Staked</div>
          <div className="col-span-1 text-center">Tasks</div>
          <div className="col-span-1 text-center">Quality</div>
          <div className="col-span-1 text-center">Memory</div>
        </div>

        {/* Rows */}
        {agents.map((agent, idx) => (
          <div key={agent.agent_address}>
            <button onClick={() => setExpanded(expanded === agent.agent_address ? null : agent.agent_address)}
              data-testid={`lb-row-${idx}`}
              className={`w-full grid grid-cols-12 gap-2 px-4 py-3 text-left transition-colors hover:bg-white/[0.02] ${idx < 3 ? 'bg-white/[0.01]' : ''} ${expanded === agent.agent_address ? 'bg-white/[0.03]' : ''}`}>
              <div className="col-span-1 flex items-center">
                <RankIcon rank={idx + 1} />
              </div>
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{agent.name}</p>
                  <p className="text-[10px] text-gray-600 font-mono truncate">{agent.agent_address.slice(0, 10)}...</p>
                </div>
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <span className="text-sm font-bold">{agent.acs}</span>
              </div>
              <div className="col-span-1 flex items-center justify-center">
                <AcsTierBadge tier={agent.tier} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-white/[0.05] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${agent.reputation_score >= 700 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : agent.reputation_score >= 500 ? 'bg-gradient-to-r from-emerald-600 to-emerald-400' : 'bg-gradient-to-r from-blue-600 to-blue-400'}`}
                    style={{ width: barWidth(agent.reputation_score, topScore) }} />
                </div>
                <span className={`text-xs font-bold w-10 text-right ${repColor(agent.reputation_score)}`}>{agent.reputation_score}</span>
              </div>
              <div className="col-span-1 flex items-center justify-center text-xs text-gray-400">{(agent.staked_kojiru / 1000).toFixed(0)}k</div>
              <div className="col-span-1 flex items-center justify-center text-xs text-gray-400">{agent.tasks_completed}</div>
              <div className="col-span-1 flex items-center justify-center text-xs text-gray-400">{agent.quality_avg > 0 ? `${(agent.quality_avg * 100).toFixed(0)}%` : '-'}</div>
              <div className="col-span-1 flex items-center justify-center text-xs text-gray-400">{agent.memory_depth}</div>
            </button>

            {/* Expanded Detail */}
            <AnimatePresence>
              {expanded === agent.agent_address && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-4 pb-4 pt-1 border-t border-white/[0.04] bg-white/[0.01]">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div className="p-2.5 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Accepted Evals</div>
                        <div className="text-sm font-bold">{agent.accepted_evaluations}/{agent.total_evaluations}</div>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Memory Packs</div>
                        <div className="text-sm font-bold">{agent.memory_packs}</div>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Work-Pods</div>
                        <div className="text-sm font-bold">{agent.work_pods}</div>
                      </div>
                      <div className="p-2.5 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Registered</div>
                        <div className="text-sm font-bold">{agent.registered_at ? new Date(agent.registered_at).toLocaleDateString() : '-'}</div>
                      </div>
                    </div>
                    {agent.capabilities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {agent.capabilities.map((c, ci) => (
                          <span key={ci} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{c}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-600 mt-2 font-mono">Full address: {agent.agent_address}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── SOP MARKETPLACE TAB ─── */
function SOPMarketplace() {
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch(`${API}/api/aaas/sops`).then(r => r.json()).then(d => { setSops(d.sops || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const categories = [...new Set(sops.map(s => s.category))];
  const filtered = filter === 'all' ? sops : sops.filter(s => s.category === filter);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === 'all' ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>All ({sops.length})</button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === c ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>{c} ({sops.filter(s => s.category === c).length})</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(sop => (
          <motion.div key={sop.sop_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
            <button onClick={() => setExpanded(expanded === sop.sop_id ? null : sop.sop_id)}
              className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`sop-${sop.sop_id}`}>
              <BookOpen className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-sm">{sop.title}</h3>
                  <TierBadge tier={sop.tier} />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{sop.description}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right hidden sm:block">
                  <div className="text-[10px] text-gray-600">{sop.views || 0} views</div>
                  <div className="text-[10px] text-gray-600">{sop.downloads || 0} downloads</div>
                </div>
                {sop.price_kojiru > 0 && <span className="text-xs font-bold text-amber-400">{sop.price_kojiru} KOJIRU</span>}
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expanded === sop.sop_id ? 'rotate-180' : ''}`} />
              </div>
            </button>
            <AnimatePresence>
              {expanded === sop.sop_id && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="px-4 pb-4 border-t border-white/[0.04]">
                    <div className="flex flex-wrap gap-1.5 my-3">
                      {(sop.tags || []).map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-500">{t}</span>)}
                    </div>
                    <pre className="text-xs text-gray-400 bg-black/30 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-[400px] overflow-y-auto">{sop.workflow_markdown}</pre>
                    <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-600">
                      <Lock className="w-3 h-3" /> Hash: {sop.content_hash?.slice(0, 16)}...
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─── MEMORY HYDRATION TAB ─── */
function MemoryHydration() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dehydrateAddr, setDehydrateAddr] = useState('');
  const [dehydrateResult, setDehydrateResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/aaas/memory/packs`).then(r => r.json()).then(d => { setPacks(d.packs || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleDehydrate = async () => {
    if (!dehydrateAddr) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/aaas/memory/dehydrate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: dehydrateAddr, max_lessons: 20 }),
      });
      if (res.ok) setDehydrateResult(await res.json());
    } catch (e) { console.error(e); }
    setProcessing(false);
  };

  return (
    <div className="space-y-8">
      {/* Dehydrate Section */}
      <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold">Dehydrate Agent Memory</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Compress an agent's experience into a portable "Lessons Learned" pack. This creates a reusable memory file that can hydrate new agents.</p>
        <div className="flex gap-3">
          <input data-testid="dehydrate-address" value={dehydrateAddr} onChange={e => setDehydrateAddr(e.target.value)}
            placeholder="Agent wallet address or ID" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
          <Button data-testid="dehydrate-btn" onClick={handleDehydrate} disabled={processing || !dehydrateAddr} className="bg-blue-600 hover:bg-blue-700 px-6">
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Database className="w-4 h-4 mr-1" /> Dehydrate</>}
          </Button>
        </div>
        {dehydrateResult && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-400">Memory Pack Created</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-500">Pack ID:</span> <span className="text-white font-mono">{dehydrateResult.pack_id}</span></div>
              <div><span className="text-gray-500">Lessons:</span> <span className="text-white">{dehydrateResult.lessons_count}</span></div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Available Packs */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Brain className="w-4 h-4 text-purple-400" /> Available Memory Packs</h3>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-gray-500 mx-auto" /> : packs.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-600">No memory packs yet. Dehydrate an agent's experience to create one.</div>
        ) : (
          <div className="space-y-2">
            {packs.map(p => (
              <div key={p.pack_id} className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <Brain className="w-5 h-5 text-purple-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{p.agent_name}</div>
                  <div className="text-[10px] text-gray-500">{p.pack_id} | {p.lessons_count} lessons | {p.hydrations} hydrations</div>
                </div>
                <span className="text-xs font-bold text-amber-400">{p.price_kojiru} KOJIRU</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── WORK-POD BUILDER TAB ─── */
function WorkPodBuilder() {
  const [form, setForm] = useState({ agent_address: '', agent_name: '', description: '', include_memory: true });
  const [sops, setSops] = useState([]);
  const [selectedSops, setSelectedSops] = useState([]);
  const [result, setResult] = useState(null);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/aaas/sops`).then(r => r.json()).then(d => setSops(d.sops || [])).catch(() => {});
  }, []);

  const toggleSop = (id) => setSelectedSops(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleBuild = async () => {
    if (!form.agent_address || !form.agent_name) return;
    setBuilding(true);
    try {
      const res = await fetch(`${API}/api/aaas/work-pods/build`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, sop_ids: selectedSops }),
      });
      if (res.ok) setResult(await res.json());
    } catch (e) { console.error(e); }
    setBuilding(false);
  };

  const handleDownload = async () => {
    if (!result?.pod_id) return;
    try {
      const res = await fetch(`${API}/api/aaas/work-pods/${result.pod_id}/download`);
      const data = await res.json();
      const bin = atob(data.zip_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-emerald-400" />
          <h3 className="font-semibold">Build a Work-Pod</h3>
        </div>
        <p className="text-xs text-gray-500 mb-5">Assemble a downloadable .zip containing identity, workflows, and memory for your agent. Compatible with LangChain, CrewAI, and AutoGPT.</p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent Address *</label>
            <input data-testid="pod-agent-address" value={form.agent_address} onChange={e => setForm({ ...form, agent_address: e.target.value })}
              placeholder="0x..." className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent Name *</label>
            <input data-testid="pod-agent-name" value={form.agent_name} onChange={e => setForm({ ...form, agent_name: e.target.value })}
              placeholder="My Credit Agent" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1">Description</label>
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="What does this agent do?" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
        </div>

        {/* SOP Selection */}
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-2">Attach SOPs from Marketplace</label>
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {sops.map(s => (
              <label key={s.sop_id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${selectedSops.includes(s.sop_id) ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]'}`}>
                <input type="checkbox" checked={selectedSops.includes(s.sop_id)} onChange={() => toggleSop(s.sop_id)} className="accent-emerald-500" />
                <div className="flex-1">
                  <div className="text-xs font-medium">{s.title}</div>
                  <div className="text-[10px] text-gray-500">{s.category}</div>
                </div>
                <TierBadge tier={s.tier} />
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 mb-5 cursor-pointer">
          <input type="checkbox" checked={form.include_memory} onChange={e => setForm({ ...form, include_memory: e.target.checked })} className="accent-emerald-500" />
          <span className="text-xs text-gray-400">Include agent memory (experience ledger)</span>
        </label>

        <Button data-testid="build-pod-btn" onClick={handleBuild} disabled={building || !form.agent_address || !form.agent_name} className="w-full bg-emerald-600 hover:bg-emerald-700 py-3">
          {building ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Building...</> : <><Package className="w-4 h-4 mr-2" /> Build Work-Pod</>}
        </Button>
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-4">
            <Check className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-emerald-400">Work-Pod Ready</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="text-center p-3 rounded-lg bg-white/[0.03]">
              <div className="text-lg font-bold text-white">{result.pod_id}</div>
              <div className="text-[10px] text-gray-500">Pod ID</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-white/[0.03]">
              <div className="text-lg font-bold text-emerald-400">{result.sop_count}</div>
              <div className="text-[10px] text-gray-500">SOPs Attached</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-white/[0.03]">
              <div className="text-lg font-bold text-blue-400">{result.memory_lessons}</div>
              <div className="text-[10px] text-gray-500">Memory Lessons</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-white/[0.03]">
              <div className="text-xs font-mono text-gray-400 break-all">{result.content_hash?.slice(0, 12)}...</div>
              <div className="text-[10px] text-gray-500">Content Hash</div>
            </div>
          </div>
          <Button data-testid="download-pod-btn" onClick={handleDownload} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 py-3">
            <Download className="w-4 h-4 mr-2" /> Download .zip Package
          </Button>
          <p className="text-[10px] text-gray-600 mt-2 text-center">Contains: identity.json, workflow.md, memory.json, README.md</p>
        </motion.div>
      )}
    </div>
  );
}

/* ─── VERIFIED DELIVERABLES TAB ─── */
function VerifiedDeliverables() {
  const [rawOutput, setRawOutput] = useState('');
  const [template, setTemplate] = useState('risk_assessment');
  const [agentAddr, setAgentAddr] = useState('');
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [copied, setCopied] = useState(false);

  const templates = [
    { id: 'risk_assessment', name: 'Risk Assessment Report' },
    { id: 'pre_qualification', name: 'Pre-Qualification Report' },
    { id: 'credit_review', name: 'Credit Line Review' },
    { id: 'compliance_checklist', name: 'Compliance Checklist' },
    { id: 'agent_dossier', name: 'Agent Dossier' },
  ];

  const handleFormat = async () => {
    if (!rawOutput || rawOutput.length < 10) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/aaas/deliverables/format`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_output: rawOutput, template, agent_address: agentAddr || undefined }),
      });
      if (res.ok) setResult(await res.json());
    } catch (e) { console.error(e); }
    setProcessing(false);
  };

  const handleCopy = () => {
    if (result?.formatted_output) {
      navigator.clipboard.writeText(result.formatted_output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-cyan-400" />
          <h3 className="font-semibold">Format Agent Output</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Paste raw agent output and transform it into a professional, institutional-grade Markdown report with a cryptographic content hash.</p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Template</label>
            <select data-testid="deliverable-template" value={template} onChange={e => setTemplate(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none">
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent Address (optional)</label>
            <input value={agentAddr} onChange={e => setAgentAddr(e.target.value)} placeholder="0x..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1">Raw Agent Output</label>
          <textarea data-testid="deliverable-raw-input" value={rawOutput} onChange={e => setRawOutput(e.target.value)}
            rows={6} placeholder="Paste the raw output from your AI agent here..."
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-emerald-500 focus:outline-none resize-y" />
        </div>

        <Button data-testid="format-btn" onClick={handleFormat} disabled={processing || rawOutput.length < 10} className="w-full bg-cyan-600 hover:bg-cyan-700 py-3">
          {processing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Formatting...</> : <><FileText className="w-4 h-4 mr-2" /> Format Deliverable</>}
        </Button>
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold">Verified Deliverable</span>
              <span className="text-[10px] text-gray-500 font-mono">{result.deliverable_id}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs border-white/10">
              {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy Markdown</>}
            </Button>
          </div>
          <pre className="text-xs text-gray-300 bg-black/30 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-[400px] overflow-y-auto">{result.formatted_output}</pre>
          <div className="flex items-center gap-2 mt-3 text-[10px] text-gray-600">
            <Lock className="w-3 h-3" /> Content Hash: {result.content_hash?.slice(0, 16)}... | Fee: {result.fee_kojiru} KOJIRU
          </div>
        </motion.div>
      )}
    </div>
  );
}


/* ─── SUBSCRIPTION PLANS TAB ─── */

/* ─── ACS LOOKUPS TAB ─── */
function ACSLookups() {
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState(null);
  const [numCredits, setNumCredits] = useState(10);
  const [agentId, setAgentId] = useState('');
  const [buyStep, setBuyStep] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);
  const [lastPurchase, setLastPurchase] = useState(null);

  const refreshBalance = async (w) => {
    try { setBalance(await getBalance(w)); } catch { /* ignore */ }
  };

  const handleConnect = async () => {
    setError(null);
    setBusy('connect');
    try {
      const w = await connectWallet();
      setWallet(w);
      await refreshBalance(w);
    } catch (e) {
      setError(e.message || 'Failed to connect wallet.');
    } finally { setBusy(''); }
  };

  const handleBuy = async () => {
    setError(null);
    setLastPurchase(null);
    setBusy('buy');
    try {
      const res = await buyCredits(numCredits, s => setBuyStep(s));
      setLastPurchase(res);
      await refreshBalance(res.buyer);
    } catch (e) {
      setError(e.code === 'ACTION_REJECTED' || e.code === 4001 ? 'Transaction canceled in wallet.' : (e.shortMessage || e.reason || e.message));
    } finally { setBusy(''); setBuyStep(''); }
  };

  const handleLookup = async () => {
    setError(null);
    setLookupResult(null);
    if (!wallet) { setError('Connect wallet first.'); return; }
    if (!/^0x[0-9a-fA-F]{40}$/.test(agentId.trim())) { setError('Enter a valid 0x… agent address (42 chars).'); return; }
    setBusy('lookup');
    try {
      const res = await lookupACS(wallet, agentId.trim());
      setLookupResult(res);
      setBalance(res.credits_remaining ?? res.balance);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(''); }
  };

  const totalCostUSDC = (numCredits * 0.5).toFixed(2);
  const buyStepLabel = {
    'connecting': 'Connecting wallet…',
    'confirming-in-wallet': 'Confirm USDC transfer in wallet…',
    'waiting-for-confirmation': 'Waiting for on-chain confirmation…',
    'crediting': 'Crediting your account…',
    'done': 'Done',
  }[buyStep] || 'Working…';

  return (
    <div className="space-y-6" data-testid="acs-lookups-tab">
      {/* Header */}
      <div className="p-5 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-cyan-300">ACS Lookups &mdash; Pay-Per-Agent Credit Reports</h2>
            <p className="text-xs text-gray-400 mt-1 max-w-2xl">
              $0.50 USDC per lookup. Get the full Agent Credit Score, tier, credit ceiling, current APR, and recent
              on-chain history for any indexed agent. Same data our MCP server hands to Claude, LangChain, and CrewAI &mdash; now available a-la-carte.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {wallet ? (
              <>
                <div className="text-xs text-gray-500 font-mono" data-testid="acs-wallet-connected">
                  {wallet.slice(0, 6)}…{wallet.slice(-4)}
                </div>
                <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-sm font-bold text-cyan-300" data-testid="acs-balance">
                  {balance ?? '…'} credits
                </div>
              </>
            ) : (
              <Button data-testid="acs-connect-wallet" onClick={handleConnect} disabled={busy === 'connect'}
                className="bg-cyan-600 hover:bg-cyan-700">
                {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect Wallet'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Buy Credits */}
      <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-emerald-400" /> Buy Lookup Credits
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-gray-500">Credits</label>
          <input data-testid="acs-num-credits" type="number" min={1} max={10000} value={numCredits}
            onChange={e => setNumCredits(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          <span className="text-xs text-gray-500">×&nbsp;$0.50&nbsp;=</span>
          <span className="text-lg font-bold text-emerald-400" data-testid="acs-total-cost">${totalCostUSDC} USDC</span>
          <Button data-testid="acs-buy-btn" onClick={handleBuy}
            disabled={!!busy || !window.ethereum}
            className="bg-emerald-600 hover:bg-emerald-700 ml-auto">
            {busy === 'buy' ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {buyStepLabel}</> : `Buy ${numCredits} Credits`}
          </Button>
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          Paid via a single USDC transfer on Base mainnet to the Kojiru treasury. No new smart contract, no merchant of record.
        </p>
      </div>

      {/* Lookup */}
      <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-cyan-400" /> Run an ACS Lookup <span className="text-xs text-gray-500 font-normal">— 1 credit</span>
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="acs-agent-input" type="text" placeholder="0xAgentAddress..." value={agentId}
            onChange={e => setAgentId(e.target.value)}
            className="flex-1 min-w-[240px] px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white font-mono placeholder:text-gray-600" />
          <Button data-testid="acs-lookup-btn" onClick={handleLookup} disabled={!!busy || !wallet}
            className="bg-cyan-600 hover:bg-cyan-700">
            {busy === 'lookup' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look Up'}
          </Button>
        </div>
        <p className="text-[10px] text-gray-600 mt-3">
          Not-found lookups automatically refund the credit &mdash; you never pay for empty results.
        </p>
      </div>

      {/* Purchase confirmation */}
      {lastPurchase && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="acs-purchase-success">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-emerald-400">Purchased {lastPurchase.credits_added} credits</p>
          </div>
          <div className="text-xs text-gray-400">
            Paid ${lastPurchase.amount_usdc.toFixed(2)} USDC. New balance: {lastPurchase.balance} credits.
          </div>
          <a href={lastPurchase.explorer} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline font-mono mt-2">
            View tx on BaseScan <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>
      )}

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3" data-testid="acs-error">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-400">Something didn&rsquo;t go through</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Lookup result */}
      {lookupResult && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-xl bg-cyan-500/5 border border-cyan-500/20" data-testid="acs-lookup-result">
          {lookupResult.found === false ? (
            <>
              <p className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Agent not indexed
              </p>
              <p className="text-xs text-gray-400 mt-2">{lookupResult.message}</p>
              {lookupResult.credit_refunded && (
                <p className="text-[10px] text-emerald-400 mt-2">✓ Credit was refunded.</p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Agent</p>
                  <p className="text-sm font-mono text-white">{lookupResult.agent_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Tier</p>
                  <p className="text-lg font-bold text-cyan-300">{lookupResult.tier}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-[10px] text-gray-500 uppercase">ACS Score</p>
                  <p className="text-2xl font-bold text-white">{lookupResult.acs_score}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-[10px] text-gray-500 uppercase">Credit Ceiling</p>
                  <p className="text-2xl font-bold text-emerald-400">${Number(lookupResult.credit_ceiling_usd).toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <p className="text-[10px] text-gray-500 uppercase">APR</p>
                  <p className="text-2xl font-bold text-amber-400">{lookupResult.apr_percent}%</p>
                </div>
              </div>
              {lookupResult.history?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Recent history</p>
                  <ul className="space-y-1">
                    {lookupResult.history.map((h, i) => (
                      <li key={i} className="text-xs text-gray-400 font-mono">
                        {h.timestamp || h.date || '—'} · {h.type || h.event || 'event'} · {h.amount_usd ? `$${h.amount_usd}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[10px] text-gray-600 mt-4">Credits remaining: {lookupResult.credits_remaining}</p>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ─── SUBSCRIPTION PLANS TAB (original) ─── */
function SubscriptionPlans() {
  const [plans, setPlans] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [step, setStep] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/aaas/subscriptions/plans`).then(r => r.json()).then(d => setPlans(d.plans)).catch(() => {});
  }, []);

  const handleSubscribe = async (planKey) => {
    if (planKey === 'free') return;
    setSubscribing(planKey);
    setError(null);
    setResult(null);
    setStep('starting');
    try {
      const out = await subscribeOnChain(planKey, s => setStep(s));
      setResult(out);
      // Best-effort: notify backend so it can queue tier activation.
      // If the endpoint doesn't exist yet, the failure is silently swallowed
      // — support will still see the tx via the SubscriptionPaid event.
      fetch(`${API}/api/aaas/subscriptions/onchain-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tx_hash: out.txHash,
          buyer: out.buyer,
          tier: out.tierName,
          amount_usdc: out.priceHuman,
        }),
      }).catch(() => {});
    } catch (e) {
      // ethers v6 wraps user rejections in ACTION_REJECTED
      if (e.code === 'ACTION_REJECTED' || e.code === 4001) {
        setError('Transaction canceled in wallet.');
      } else {
        setError(e.shortMessage || e.reason || e.message || 'Subscription failed. Please try again.');
      }
    } finally {
      setSubscribing(null);
      setStep('');
    }
  };

  if (!plans) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  const planConfigs = [
    { key: 'free', color: 'gray', accent: 'text-gray-400', border: 'border-white/[0.06]', bg: 'bg-white/[0.02]', popular: false },
    { key: 'pro', color: 'emerald', accent: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/5', popular: true },
    { key: 'enterprise', color: 'amber', accent: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/5', popular: false },
  ];

  const featureLabel = (val) => val === -1 ? 'Unlimited' : val === true ? 'Yes' : val === false ? '-' : String(val);

  const stepLabel = {
    'starting': 'Preparing…',
    'connecting': 'Connecting wallet…',
    'reading-price': 'Reading on-chain price…',
    'approving-usdc': 'Approve USDC in wallet…',
    'subscribing': 'Confirm subscribe tx in wallet…',
    'confirmed': 'Confirmed',
  }[step] || null;

  return (
    <div className="space-y-6">
      {/* On-chain checkout notice */}
      <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20" data-testid="subscribe-onchain-notice">
        <p className="text-sm text-emerald-300 font-semibold mb-1">
          On-chain USDC checkout — live on Base
        </p>
        <p className="text-xs text-gray-400 leading-relaxed">
          Kojiru subscriptions are settled directly by <a href="https://basescan.org/address/0x52fcA0998076b599c932F1647137DCBEb78FA839" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline font-mono">KojiruSubscription.sol</a> on Base mainnet.
          A single USDC transaction — no merchant-of-record, no card processor. Bridge USDC to Base first if you haven&rsquo;t already
          (Coinbase, Bridge.base.org, or any Base-supporting exchange). Your tier activates within minutes of tx confirmation.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-5">
        {planConfigs.map(({ key, accent, border, bg, popular }) => {
          const p = plans[key];
          return (
            <div key={key} className={`relative p-6 rounded-xl ${bg} border ${border}`} data-testid={`plan-${key}`}>
              {popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-emerald-600 text-[10px] font-bold text-white uppercase tracking-wider">Most Popular</div>}
              <h3 className={`text-xl font-bold ${accent}`}>{p.name}</h3>
              <div className="mt-2 mb-5">
                {p.price_monthly_usd > 0 ? (
                  <>
                    <span className="text-3xl font-bold text-white">${p.price_monthly_usd}</span>
                    <span className="text-sm text-gray-500"> USDC/mo</span>
                    <p className="text-xs text-gray-600 mt-0.5">Paid in USDC on Base — 30-day access</p>
                  </>
                ) : <span className="text-3xl font-bold text-white">Free</span>}
              </div>
              <ul className="space-y-2 mb-6">
                {[
                  { label: 'Deliverables/mo', val: p.deliverables_per_month },
                  { label: 'Hydrations/mo', val: p.hydrations_per_month },
                  { label: 'Work-Pods/mo', val: p.work_pods_per_month },
                  { label: 'SOP Access', val: p.sop_access === 'all' ? 'All tiers' : 'Free only' },
                  { label: 'API Access', val: p.api_access },
                  { label: 'Bulk Hydration', val: p.bulk_hydration },
                  { label: 'White Label', val: p.white_label },
                  { label: 'Custom SOPs', val: p.custom_sops },
                  { label: 'Priority Support', val: p.priority_support },
                ].map(f => (
                  <li key={f.label} className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{f.label}</span>
                    <span className={`font-medium ${f.val === true || f.val === -1 || f.val === 'All tiers' ? accent : 'text-gray-600'}`}>{featureLabel(f.val)}</span>
                  </li>
                ))}
              </ul>
              <Button data-testid={`subscribe-${key}`} onClick={() => handleSubscribe(key)} disabled={subscribing !== null || key === 'free'}
                className={`w-full py-2.5 ${popular ? 'bg-emerald-600 hover:bg-emerald-700' : key === 'free' ? 'bg-white/10' : 'bg-white/10 hover:bg-white/15'}`}>
                {subscribing === key ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {stepLabel || 'Working…'}</> :
                  key === 'free' ? 'Current Plan' :
                  key === 'pro' ? `Pay $${p.price_monthly_usd} USDC` : `Pay $${p.price_monthly_usd} USDC`}
              </Button>
            </div>
          );
        })}
      </div>

      {error && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3"
          data-testid="subscribe-error">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-400">Subscription didn&rsquo;t complete</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        </motion.div>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
          data-testid="subscribe-success">
          <div className="flex items-center gap-2 mb-3">
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-sm font-semibold text-emerald-400">Subscription payment confirmed on Base</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs mb-3">
            <div><span className="text-gray-500">Tier:</span> <span className="text-white font-semibold uppercase">{result.tierName}</span></div>
            <div><span className="text-gray-500">Paid:</span> <span className="text-white font-semibold">${result.priceHuman} USDC</span></div>
          </div>
          <a href={result.explorer} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:underline font-mono">
            View tx on BaseScan <ExternalLink className="w-3 h-3" />
          </a>
          <p className="text-[10px] text-gray-500 mt-3">
            Your tier activation is queued. If it hasn&rsquo;t applied within 10 minutes, contact support with the tx hash above.
          </p>
        </motion.div>
      )}
    </div>
  );
}

/* ─── MEMORY MARKETPLACE TAB ─── */
function MemoryMarketplace() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null);

  const fetchListings = () => {
    fetch(`${API}/api/aaas/marketplace/listings`).then(r => r.json()).then(d => { setListings(d.listings || []); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchListings(); }, []);

  const handlePurchase = async (listingId) => {
    setPurchasing(listingId);
    try {
      const res = await fetch(`${API}/api/aaas/marketplace/purchase`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_id: listingId, buyer_address: '0xDemoBuyer' }),
      });
      if (res.ok) {
        const data = await res.json();
        setPurchaseResult(data);
        fetchListings();
      }
    } catch (e) { console.error(e); }
    setPurchasing(null);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/5 to-blue-500/5 border border-purple-500/20">
        <h3 className="font-semibold mb-1">How the Memory Marketplace Works</h3>
        <p className="text-xs text-gray-400">Agent operators dehydrate their best agents' experience into sellable memory packs. Buyers purchase these packs to instantly hydrate new agents with proven expertise. Revenue splits 70/30 — publisher gets 70%, Kojiru takes 30%.</p>
      </div>

      {listings.length === 0 ? (
        <div className="text-center py-12">
          <Brain className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-1">No memory packs listed yet</p>
          <p className="text-xs text-gray-600">Dehydrate an agent's experience and list it on the Memory Hydration tab.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map(l => (
            <div key={l.listing_id} className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid={`listing-${l.listing_id}`}>
              <Brain className="w-6 h-6 text-purple-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{l.agent_name || 'Agent Experience Pack'}</div>
                <p className="text-xs text-gray-500 truncate">{l.description}</p>
                <div className="flex gap-3 mt-1 text-[10px] text-gray-600">
                  <span>{l.lessons_count} lessons</span>
                  <span>{l.purchases} sales</span>
                  <span>Publisher: {l.royalty_pct}%</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-bold text-amber-400">{l.price_kojiru}</div>
                <div className="text-[10px] text-gray-500">KOJIRU</div>
              </div>
              <Button size="sm" onClick={() => handlePurchase(l.listing_id)} disabled={purchasing === l.listing_id}
                className="bg-purple-600 hover:bg-purple-700 px-4 shrink-0" data-testid={`buy-${l.listing_id}`}>
                {purchasing === l.listing_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Buy'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {purchaseResult && (
        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-400">Purchase Complete</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><span className="text-gray-500">To Publisher:</span> <span className="text-emerald-400 font-bold">{purchaseResult.kojiru_to_publisher} KOJIRU</span></div>
            <div><span className="text-gray-500">To Platform:</span> <span className="text-amber-400 font-bold">{purchaseResult.kojiru_to_platform} KOJIRU</span></div>
            <div><span className="text-gray-500">Split:</span> <span className="text-white">{purchaseResult.royalty_pct}% / {100 - purchaseResult.royalty_pct}%</span></div>
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Memory injected into buyer agent. {purchaseResult.purchase_id}</p>
        </motion.div>
      )}
    </div>
  );
}

/* ─── CERTIFICATION TAB ─── */
function CertificationTab() {
  const [checkAddr, setCheckAddr] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [certResult, setCertResult] = useState(null);
  const [certified, setCertified] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/aaas/certification/verified`).then(r => r.json()).then(d => setCertified(d.certified_agents || [])).catch(() => {});
  }, []);

  const handleCheck = async () => {
    if (!checkAddr) return;
    setChecking(true); setEligibility(null); setCertResult(null);
    try {
      const res = await fetch(`${API}/api/aaas/certification/check/${checkAddr}`);
      if (res.ok) setEligibility(await res.json());
      else { const err = await res.json().catch(() => ({})); setEligibility({ error: err.detail || 'Agent not found' }); }
    } catch (e) { setEligibility({ error: 'Failed to check' }); }
    setChecking(false);
  };

  const handleCertify = async () => {
    setCertifying(true);
    try {
      const res = await fetch(`${API}/api/aaas/certification/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: checkAddr }),
      });
      if (res.ok) {
        const data = await res.json();
        setCertResult(data);
        setCertified(prev => [data, ...prev]);
      } else {
        const err = await res.json().catch(() => ({}));
        setCertResult({ error: err.detail || 'Certification failed' });
      }
    } catch (e) { setCertResult({ error: 'Failed' }); }
    setCertifying(false);
  };

  return (
    <div className="space-y-8">
      {/* Requirements */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-amber-500/5 to-emerald-500/5 border border-amber-500/20">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold">Kojiru Certified Badge</h3>
        </div>
        <p className="text-xs text-gray-400 mb-4">Certified agents are verified as high-performers. Lenders can filter for certified agents only — creating premium demand and trust.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Min ACS', value: '740+', icon: TrendingUp },
            { label: 'Min Tasks', value: '1+', icon: Star },
            { label: 'Min Staked', value: '10,000 USDC', icon: Lock },
            { label: 'Fee', value: '200 KOJIRU', icon: Tag },
          ].map(r => (
            <div key={r.label} className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.05] text-center">
              <r.icon className="w-4 h-4 text-amber-400 mx-auto mb-1" />
              <div className="text-sm font-bold text-white">{r.value}</div>
              <div className="text-[10px] text-gray-500">{r.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Check Eligibility */}
      <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="font-semibold mb-3">Check Eligibility</h3>
        <div className="flex gap-3">
          <input data-testid="cert-check-addr" value={checkAddr} onChange={e => setCheckAddr(e.target.value)}
            placeholder="Agent wallet address" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-amber-500 focus:outline-none" />
          <Button data-testid="cert-check-btn" onClick={handleCheck} disabled={checking || !checkAddr} className="bg-amber-600 hover:bg-amber-700 px-6">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
          </Button>
        </div>

        {eligibility?.error && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400">{eligibility.error}</div>
        )}

        {eligibility && !eligibility.error && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
            {eligibility.already_certified ? (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                <span className="text-sm font-semibold text-amber-400">Already Kojiru Certified</span>
              </div>
            ) : (
              <>
                <div className={`p-3 rounded-lg border ${eligibility.eligible ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <p className={`text-sm font-semibold ${eligibility.eligible ? 'text-emerald-400' : 'text-red-400'}`}>
                    {eligibility.eligible ? 'Eligible for Certification' : 'Not Yet Eligible'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{eligibility.agent_name}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(eligibility.checks || {}).map(([k, v]) => (
                    <div key={k} className={`p-2.5 rounded-lg text-center ${v ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      {v ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : <span className="text-red-400 text-sm font-bold">X</span>}
                      <p className="text-[10px] text-gray-400 mt-0.5">{k.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
                {eligibility.eligible && (
                  <Button data-testid="cert-apply-btn" onClick={handleCertify} disabled={certifying} className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:brightness-110 py-3">
                    {certifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Certifying...</> : <><Award className="w-4 h-4 mr-2" /> Apply for Certification — 200 KOJIRU</>}
                  </Button>
                )}
              </>
            )}
          </motion.div>
        )}

        {certResult && !certResult.error && (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center">
            <Award className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-lg font-bold text-amber-400">Kojiru Certified</p>
            <p className="text-sm text-white">{certResult.agent_name}</p>
            <p className="text-[10px] text-gray-500 mt-1 font-mono">{certResult.certification_id} | Hash: {certResult.certification_hash?.slice(0, 16)}...</p>
          </motion.div>
        )}
      </div>

      {/* Certified Agents List */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-amber-400" /> Kojiru Certified Agents ({certified.length})</h3>
        {certified.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-600">No certified agents yet. Be the first.</div>
        ) : (
          <div className="space-y-2">
            {certified.map(c => (
              <div key={c.certification_id} className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid={`cert-${c.certification_id}`}>
                <Award className="w-5 h-5 text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{c.agent_name}</div>
                  <div className="text-[10px] text-gray-500">{c.certification_id} | ACS {c.acs_at_certification} | Staked {c.staked_at_certification?.toLocaleString()} KOJIRU</div>
                </div>
                <div className="text-xs text-gray-500">{c.certified_at ? new Date(c.certified_at).toLocaleDateString() : ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── MAIN PAGE ─── */
export default function AaaSPage() {
  const [active, setActive] = useState('leaderboard');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/aaas/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet>
        <title>Kojiru AaaS — Agent-as-a-Service | Work-Pods, SOPs, Memory</title>
        <meta name="description" content="The Workstation and Corporate Memory for the Machine Economy. SOP Marketplace, Memory Hydration, Work-Pod Builder, and Verified Deliverables." />
      </Helmet>
      <Navbar />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <Package className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">Agent-as-a-Service</span>
            </div>
            <h1 data-testid="aaas-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 tracking-tight">
              The Workstation for{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Digital Employees.</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Beyond capital, agents need workflows, memory, and institutional-grade deliverables.
              Kojiru AaaS provides the workstation and corporate memory for the machine economy.
            </p>
          </motion.div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
              {[
                { label: 'Published SOPs', value: stats.sops_published, color: 'text-emerald-400' },
                { label: 'Memory Packs', value: stats.memory_packs, color: 'text-blue-400' },
                { label: 'Hydrations', value: stats.total_hydrations, color: 'text-purple-400' },
                { label: 'Deliverables', value: stats.deliverables_formatted, color: 'text-cyan-400' },
                { label: 'Work-Pods', value: stats.work_pods_built, color: 'text-amber-400' },
                { label: 'Subscriptions', value: stats.active_subscriptions, color: 'text-teal-400' },
                { label: 'Listings', value: stats.memory_listings, color: 'text-indigo-400' },
                { label: 'Certified', value: stats.certified_agents, color: 'text-yellow-400' },
              ].map(s => (
                <div key={s.label} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] text-center">
                  <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="aaas-tabs">
            {TABS.map(t => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActive(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  active === t.id ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}>
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          {/* Active Tab Content */}
          <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {active === 'leaderboard' && <ReputationLeaderboard />}
            {active === 'marketplace' && <SOPMarketplace />}
            {active === 'memory-market' && <MemoryMarketplace />}
            {active === 'certification' && <CertificationTab />}
            {active === 'subscriptions' && <SubscriptionPlans />}
            {active === 'acs-lookups' && <ACSLookups />}
            {active === 'memory' && <MemoryHydration />}
            {active === 'workpod' && <WorkPodBuilder />}
            {active === 'deliverables' && <VerifiedDeliverables />}
          </motion.div>

          {/* Revenue Model Section */}
          <section className="mt-16 pt-12 border-t border-white/[0.04]">
            <h2 className="text-2xl font-bold text-center mb-2">AaaS Revenue Model</h2>
            <p className="text-sm text-gray-500 text-center mb-8">Five revenue streams powering the machine economy.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
              {[
                { name: 'Subscriptions', subtitle: 'Recurring Revenue', icon: Zap, color: 'emerald',
                  items: ['Free / Pro $99/mo / Enterprise $999/mo', 'Gated features per tier', 'Predictable monthly revenue'] },
                { name: 'SOP Marketplace', subtitle: 'Per-Purchase', icon: BookOpen, color: 'teal',
                  items: ['Free / 25 / 100 KOJIRU per SOP', 'Third-party publishers: 70/30 split', 'Growing library of validated workflows'] },
                { name: 'Memory Market', subtitle: 'Royalties', icon: Brain, color: 'purple',
                  items: ['Publisher sets price', '70% to publisher, 30% to Kojiru', 'Recurring royalties on resale'] },
                { name: 'Certification', subtitle: 'Premium Badge', icon: Award, color: 'amber',
                  items: ['200 KOJIRU per certification', 'Creates lender demand filter', 'Network effect: more certified = more trust'] },
                { name: 'Deliverables', subtitle: 'Per-Document', icon: FileText, color: 'cyan',
                  items: ['5 KOJIRU per formatted report', '5 institutional templates', 'Cryptographic content signing'] },
              ].map(tier => (
                <div key={tier.name} className={`p-5 rounded-xl bg-${tier.color}-500/5 border border-${tier.color}-500/20`}>
                  <tier.icon className={`w-6 h-6 text-${tier.color}-400 mb-2`} />
                  <h3 className="text-base font-bold">{tier.name}</h3>
                  <p className={`text-[10px] text-${tier.color}-400 mb-3`}>{tier.subtitle}</p>
                  <ul className="space-y-1.5">
                    {tier.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-gray-400">
                        <ChevronRight className={`w-3 h-3 text-${tier.color}-500 shrink-0 mt-0.5`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
